import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type NotificationIntent = 'info' | 'success' | 'warning' | 'error';

export interface NotificationOptions {
  message: ReactNode;
  intent?: NotificationIntent;
  /** ms before auto-dismiss. Pass 0 to require manual dismiss. Default 4000. */
  durationMs?: number;
  /** Optional title shown bold in the message bar. */
  title?: string;
}

export interface ActiveNotification extends Required<Omit<NotificationOptions, 'title'>> {
  id: string;
  title?: string;
}

export interface NotificationContextValue {
  notifications: ActiveNotification[];
  notify: (options: NotificationOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const DEFAULT_DURATION_MS = 4000;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<ActiveNotification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((options: NotificationOptions) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const intent = options.intent ?? 'info';
    const durationMs = options.durationMs ?? DEFAULT_DURATION_MS;

    setNotifications(prev => [
      ...prev,
      {
        id,
        message: options.message,
        title: options.title,
        intent,
        durationMs,
      },
    ]);

    if (durationMs > 0) {
      const timer = setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timer);
    }

    return id;
  }, [dismiss]);

  const clear = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  const value = useMemo<NotificationContextValue>(
    () => ({ notifications, notify, dismiss, clear }),
    [notifications, notify, dismiss, clear],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used inside <NotificationProvider>.');
  }
  return ctx;
}
